/**
 * Tags tab.
 *
 * The tenant's NFC/QR tags are read-only here — the super-admin generates
 * them — so this is a reference list: which tags exist, which are still free,
 * and what each assigned one is stuck to.
 *
 * It replaces a modal that showed the same three columns with no search,
 * no filter and no export, which stopped being readable past ~50 tags.
 */

import { t } from '../i18n.js'
import { escapeHtml } from '../utils.js'

function refreshIcons() {
  if (typeof lucide !== 'undefined') lucide.createIcons()
}

let tagsCache = []
let tagFilters = { search: '', status: '' }

// ==================== LOAD ====================

export async function loadTags() {
  const container = document.getElementById('tagsList')
  if (!container) return

  container.innerHTML = `<p class="loading">${escapeHtml(t('common.loading'))}</p>`

  try {
    const { supabase, currentUser } = await import('./main.js')
    if (!currentUser?.tenant_id) return

    const { data, error } = await supabase
      .from('nfc_tags')
      .select('nfc_id, nfc_url, url_code, is_assigned, equipment:equipment_id (name)')
      .eq('tenant_id', currentUser.tenant_id)
      .order('nfc_id', { ascending: true })

    if (error) throw error

    tagsCache = data || []
    renderTags()
  } catch (error) {
    console.error('Error loading tags:', error)
    container.innerHTML = `<p class="error-message show">${escapeHtml(error.message || t('common.error'))}</p>`
  }
}

/** Apply the search box and the status filter. */
function filteredTags() {
  const search = tagFilters.search.trim().toLowerCase()

  return tagsCache.filter(tag => {
    if (tagFilters.status === 'free' && tag.is_assigned) return false
    if (tagFilters.status === 'assigned' && !tag.is_assigned) return false

    if (!search) return true
    return [tag.nfc_id, tag.nfc_url, tag.equipment?.name]
      .some(field => (field || '').toLowerCase().includes(search))
  })
}

function renderTags() {
  const container = document.getElementById('tagsList')
  const counter = document.getElementById('tagsCounter')
  if (!container) return

  const rows = filteredTags()
  const free = tagsCache.filter(tag => !tag.is_assigned).length

  if (counter) {
    counter.textContent = t('tags.counter')
      .replace('{shown}', rows.length)
      .replace('{total}', tagsCache.length)
      .replace('{free}', free)
  }

  if (tagsCache.length === 0) {
    container.innerHTML = `<p class="text-muted">${escapeHtml(t('tags.noTags'))}</p>`
    return
  }

  if (rows.length === 0) {
    container.innerHTML = `<p class="text-muted">${escapeHtml(t('equipment.noResults'))}</p>`
    return
  }

  container.innerHTML = `
    <div class="tags-table-wrap">
      <table class="tags-table">
        <thead>
          <tr>
            <th>${escapeHtml(t('tags.columnCode'))}</th>
            <th>${escapeHtml(t('tags.columnUrl'))}</th>
            <th>${escapeHtml(t('tags.columnStatus'))}</th>
            <th>${escapeHtml(t('equipment.title'))}</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(tag => `
            <tr>
              <td><strong>${escapeHtml(tag.nfc_id)}</strong></td>
              <td><code class="tag-url">${escapeHtml(tag.nfc_url)}</code></td>
              <td>
                <span class="tag-status ${tag.is_assigned ? 'tag-assigned' : 'tag-available'}">
                  ${escapeHtml(tag.is_assigned ? t('tags.statusAssigned') : t('tags.statusAvailable'))}
                </span>
              </td>
              <td>${escapeHtml(tag.equipment?.name || '—')}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    </div>`

  refreshIcons()
}

// ==================== EXPORT ====================

/** Export whatever the filter currently shows, matching the equipment tab. */
async function exportTags(btn) {
  if (typeof ExcelJS === 'undefined') {
    alert('ExcelJS is not loaded')
    return
  }

  const rows = filteredTags()
  if (rows.length === 0) {
    alert(t('equipment.exportNoData'))
    return
  }

  const original = btn?.innerHTML
  if (btn) {
    btn.disabled = true
    btn.textContent = t('equipment.exporting')
  }

  try {
    const { currentUser } = await import('./main.js')

    const workbook = new ExcelJS.Workbook()
    const sheet = workbook.addWorksheet(t('tags.modalTitle'))

    sheet.columns = [
      { header: t('tags.columnCode'), key: 'code', width: 26 },
      { header: t('tags.columnUrl'), key: 'url', width: 46 },
      { header: t('tags.columnStatus'), key: 'status', width: 14 },
      { header: t('equipment.title'), key: 'equipment', width: 30 },
    ]

    rows.forEach(tag => sheet.addRow({
      code: tag.nfc_id,
      url: tag.nfc_url,
      status: tag.is_assigned ? t('tags.statusAssigned') : t('tags.statusAvailable'),
      equipment: tag.equipment?.name || '-',
    }))

    sheet.getRow(1).font = { bold: true }
    sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } }
    sheet.views = [{ state: 'frozen', ySplit: 1 }]
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columns.length } }

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const company = (currentUser?.tenant?.company_name || 'tags')
      .replace(/[^\p{L}\p{N}]+/gu, '_').replace(/^_+|_+$/g, '')
    a.href = url
    a.download = `${company}_tags_${new Date().toISOString().substring(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Tags export failed:', error)
    alert(error.message || t('common.error'))
  } finally {
    if (btn) {
      btn.disabled = false
      btn.innerHTML = original
    }
  }
}

// ==================== WIRING ====================

export function initTags() {
  document.getElementById('tagsSearchInput')?.addEventListener('input', (e) => {
    tagFilters.search = e.target.value
    renderTags()
  })

  document.querySelectorAll('.tags-status-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.tags-status-btn').forEach(b => b.classList.remove('active'))
      btn.classList.add('active')
      tagFilters.status = btn.dataset.status || ''
      renderTags()
    })
  })

  document.getElementById('exportTagsBtn')?.addEventListener('click', (e) => {
    exportTags(e.currentTarget)
  })
}
